import React, { useState } from 'react';

const AccessibilityBar = ({
    settings,
    onSettingsChange,
    className = ''
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleSetting = (key) => {
        onSettingsChange({
            ...settings,
            [key]: !settings[key]
        });
    };

    const accessibilityOptions = [
        {
            key: 'reduceMotion',
            label: 'Reduce Motion',
            icon: '🔄',
            description: 'Minimizes visual movement and animations'
        },
        {
            key: 'showCaptions',
            label: 'Captions',
            icon: '💬',
            description: 'Show practice guidance captions'
        },
        {
            key: 'highContrast',
            label: 'High Contrast',
            icon: '🔆',
            description: 'Enhanced contrast for better visibility'
        },
        {
            key: 'dyslexicFont',
            label: 'Dyslexic Font',
            icon: '🔤',
            description: 'Use dyslexic-friendly font styling'
        }
    ];

    return (
        <div className={`${className}`}>
            {/* Toggle Button */}
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="bg-gray-900 bg-opacity-75 hover:bg-opacity-90 px-3 py-2 rounded-lg text-white transition-all flex items-center gap-2"
                title="Accessibility Options"
            >
                ♿ <span className="text-sm">A11y</span>
                <span className={`transform transition-transform text-xs ${isExpanded ? 'rotate-180' : ''}`}>
                    ▼
                </span>
            </button>

            {/* Accessibility Panel */}
            {isExpanded && (
                <div className="mt-2 bg-gray-900 bg-opacity-90 rounded-lg p-4 min-w-72 backdrop-blur-sm">
                    <h3 className="text-white text-sm font-semibold mb-3 flex items-center gap-2">
                        ♿ Accessibility Settings
                    </h3>

                    {/* Options List */}
                    <div className="space-y-3">
                        {accessibilityOptions.map((option) => (
                            <div
                                key={option.key}
                                className="flex items-center justify-between"
                            >
                                <div className="flex items-center gap-3 flex-1">
                                    <span className="text-lg">{option.icon}</span>
                                    <div>
                                        <div className="text-white text-sm font-medium">
                                            {option.label}
                                        </div>
                                        <div className="text-gray-400 text-xs">
                                            {option.description}
                                        </div>
                                    </div>
                                </div>

                                {/* Toggle Switch */}
                                <button
                                    onClick={() => toggleSetting(option.key)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${
                                        settings[option.key]
                                            ? 'bg-blue-600'
                                            : 'bg-gray-600'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                            settings[option.key]
                                                ? 'translate-x-6'
                                                : 'translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Quick Actions */}
                    <div className="border-t border-gray-700 mt-4 pt-3">
                        <div className="text-xs text-gray-400 mb-2">Quick Actions</div>
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    // Minimal mode: reduce motion, show captions, no high contrast
                                    onSettingsChange({
                                        ...settings,
                                        reduceMotion: true,
                                        showCaptions: true,
                                        highContrast: false,
                                        dyslexicFont: false
                                    });
                                }}
                                className="text-xs bg-blue-600 bg-opacity-50 hover:bg-opacity-70 px-2 py-1 rounded transition-colors text-white"
                            >
                                Minimal
                            </button>
                            <button
                                onClick={() => {
                                    // Full accessibility mode
                                    onSettingsChange({
                                        ...settings,
                                        reduceMotion: true,
                                        showCaptions: true,
                                        highContrast: true,
                                        dyslexicFont: true
                                    });
                                }}
                                className="text-xs bg-green-600 bg-opacity-50 hover:bg-opacity-70 px-2 py-1 rounded transition-colors text-white"
                            >
                                Max A11y
                            </button>
                            <button
                                onClick={() => {
                                    // Reset to defaults
                                    onSettingsChange({
                                        ...settings,
                                        reduceMotion: false,
                                        showCaptions: true,
                                        highContrast: false,
                                        dyslexicFont: false
                                    });
                                }}
                                className="text-xs bg-gray-600 bg-opacity-50 hover:bg-opacity-70 px-2 py-1 rounded transition-colors text-white"
                            >
                                Default
                            </button>
                        </div>
                    </div>

                    {/* Screen Reader Support Info */}
                    <div className="border-t border-gray-700 mt-3 pt-3">
                        <div className="text-xs text-gray-400">
                            <p className="mb-1">🔊 Screen reader compatible</p>
                            <p className="mb-1">⌨️ Keyboard navigation supported</p>
                            <p>🎧 Audio descriptions available</p>
                        </div>
                    </div>

                    {/* Keyboard Shortcuts */}
                    <div className="border-t border-gray-700 mt-3 pt-3">
                        <div className="text-xs text-gray-400 mb-2">Keyboard Shortcuts</div>
                        <div className="space-y-1 text-xs">
                            <div className="flex justify-between">
                                <span>Play/Pause</span>
                                <span className="text-gray-300">Space</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Stop</span>
                                <span className="text-gray-300">Esc</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Toggle Captions</span>
                                <span className="text-gray-300">C</span>
                            </div>
                            <div className="flex justify-between">
                                <span>Fullscreen</span>
                                <span className="text-gray-300">F</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccessibilityBar;